<template>
  <!-- Was an inline div rendered inside the app shell, which is a flex
       container — `column` on mobile, `row` at md. As a flex item it fell to
       the bottom of the page on phones and stretched into a full-height second
       column beside <main> on desktop. AppBanner teleports out of that layout
       and pins itself with --z-banner. -->
  <AppBanner
    v-model="showUpgrade"
    :title="t('account_upgrade.banner_title')"
    :message="t('account_upgrade.banner_description')"
  >
    <template #actions>
      <AppButton size="sm" @click="showUpgradeModal = true">
        {{ t('account_upgrade.upgrade_account') }}
      </AppButton>
      <AppButton variant="link" @click="dismissUpgrade">
        {{ t('account_upgrade.maybe_later') }}
      </AppButton>
    </template>
  </AppBanner>

  <!-- Upgrade Modal -->
  <AppModal
    v-model="showUpgradeModal"
    :title="t('account_upgrade.modal_title')"
    :mobile-sheet="false"
    @close="closeUpgradeModal"
  >
    <div class="mb-8">
      <p class="label-caps mb-4">{{ t('account_upgrade.why_upgrade') }}</p>
      <ul class="list-none p-0 m-0">
        <li class="flex items-center gap-3 py-2" style="font-size: var(--text-sm); color: var(--text-secondary); border-bottom: 1px solid var(--border-subtle);">
          <span style="color: var(--forest-600); font-size: var(--text-xs);">&check;</span>
          {{ t('account_upgrade.benefit_sync') }}
        </li>
        <li class="flex items-center gap-3 py-2" style="font-size: var(--text-sm); color: var(--text-secondary); border-bottom: 1px solid var(--border-subtle);">
          <span style="color: var(--forest-600); font-size: var(--text-xs);">&check;</span>
          {{ t('account_upgrade.benefit_never_lose') }}
        </li>
        <li class="flex items-center gap-3 py-2" style="font-size: var(--text-sm); color: var(--text-secondary); border-bottom: 1px solid var(--border-subtle);">
          <span style="color: var(--forest-600); font-size: var(--text-xs);">&check;</span>
          {{ t('account_upgrade.benefit_premium') }}
        </li>
        <li class="flex items-center gap-3 py-2" style="font-size: var(--text-sm); color: var(--text-secondary);">
          <span style="color: var(--forest-600); font-size: var(--text-xs);">&check;</span>
          {{ t('account_upgrade.benefit_ai') }}
        </li>
      </ul>
    </div>

    <!-- Social Upgrade Options -->
    <div class="mb-6">
      <AppButton variant="outline" block :disabled="isLoading" @click="handleGoogleUpgrade">
        <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        {{ t('account_upgrade.continue_google') }}
      </AppButton>
    </div>

    <AppDivider :label="t('common.or')" />

    <!-- Email Registration Form -->
    <form @submit.prevent="handleEmailUpgrade" class="mb-6">
      <div class="mb-4">
        <AppInput
          v-model="form.displayName"
          type="text"
          name="name"
          autocomplete="name"
          :label="t('auth.full_name')"
          :placeholder="t('account_upgrade.full_name_placeholder')"
          required
          :disabled="isLoading"
        />
      </div>

      <div class="mb-4">
        <AppInput
          v-model="form.email"
          type="email"
          name="email"
          autocomplete="email"
          inputmode="email"
          :label="t('auth.email')"
          :placeholder="t('account_upgrade.email_placeholder')"
          required
          :disabled="isLoading"
        />
      </div>

      <div class="mb-4">
        <AppInput
          v-model="form.password"
          type="password"
          name="new-password"
          autocomplete="new-password"
          :label="t('auth.password')"
          :placeholder="t('account_upgrade.password_placeholder')"
          required
          :disabled="isLoading"
          :minlength="6"
        />
      </div>

      <div v-if="error" class="p-2 mb-4" style="background: var(--ruby-100); color: var(--ruby-700); border-radius: 3px; font-size: var(--text-xs); border: 1px solid var(--ruby-200);">
        {{ error }}
      </div>

      <AppButton type="submit" block :loading="isLoading" :disabled="!isFormValid">
        {{ t('account_upgrade.upgrade_account') }}
      </AppButton>
    </form>

    <div class="text-center" style="font-size: var(--text-xs); color: var(--text-tertiary);">
      <p class="m-0">{{ t('account_upgrade.data_preserved') }}</p>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { isAnonymous, linkAnonymousWithEmail, linkAnonymousWithGoogle } from "../stores/authStore"
import { localizeAuthError } from "../utils/authErrors"
import { AppBanner, AppButton, AppDivider, AppInput, AppModal } from "./ui"

const { t } = useI18n()

interface Emits {
  (e: "upgraded"): void
}

const emit = defineEmits<Emits>()

const showUpgrade = ref(false)
const showUpgradeModal = ref(false)
const isLoading = ref(false)
const error = ref<string | null>(null)
const dismissed = ref(false)

const form = ref({
  email: "",
  password: "",
  displayName: "",
})

const isFormValid = computed(() => {
  const emailValid = form.value.email.includes("@")
  const passwordValid = form.value.password.length >= 6
  const nameValid = form.value.displayName.trim().length > 0

  return emailValid && passwordValid && nameValid
})

// Show upgrade banner for anonymous users (but not immediately)
onMounted(() => {
  if (isAnonymous.value && !dismissed.value) {
    // Show banner after 30 seconds of usage
    setTimeout(() => {
      if (isAnonymous.value && !dismissed.value) {
        showUpgrade.value = true
      }
    }, 30000)
  }
})

function dismissUpgrade() {
  showUpgrade.value = false
  dismissed.value = true
  // Show again after 24 hours
  setTimeout(
    () => {
      dismissed.value = false
    },
    24 * 60 * 60 * 1000,
  )
}

function closeUpgradeModal() {
  showUpgradeModal.value = false
  error.value = null
}

// Let App.vue (profile "Upgrade now", AI feature gates) open the linking
// modal directly — linking preserves the guest's data, unlike AuthModal's
// plain register/login which would orphan it.
function openModal() {
  showUpgrade.value = false
  showUpgradeModal.value = true
}

defineExpose({ openModal })

async function handleGoogleUpgrade() {
  isLoading.value = true
  error.value = null

  try {
    await linkAnonymousWithGoogle()
    emit("upgraded")
    showUpgrade.value = false
    showUpgradeModal.value = false
  } catch (err: any) {
    error.value = localizeAuthError(err, t)
  } finally {
    isLoading.value = false
  }
}

async function handleEmailUpgrade() {
  if (!isFormValid.value) return

  isLoading.value = true
  error.value = null

  try {
    await linkAnonymousWithEmail({
      email: form.value.email,
      password: form.value.password,
      displayName: form.value.displayName.trim() || undefined,
    })

    emit("upgraded")
    showUpgrade.value = false
    showUpgradeModal.value = false
  } catch (err: any) {
    error.value = localizeAuthError(err, t)
  } finally {
    isLoading.value = false
  }
}
</script>
